import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableLoading } from "@/components/ui/table";
import PaginationButton from "@/components/ui/pagination-button";
import React from "react";
import {Route} from "@/routes/(pharma)/_layout/reports/purchase-requests-list/index"
import { useRouter } from "@tanstack/react-router";
import { RequestStatus } from "@/gen/purchase_request/common_pb";
import dayjs from "dayjs";
import { usePurchaseRequestsData } from "../-hooks/usePurchaseRequestData";

const StyledTableCell = ({ className = "", ...props }) => (
  <TableCell className={"font-medium " + className} {...props} />
);
const StyledTableHead = ({ className = "", ...props }) => (
  <TableHead className={"font-medium " + className} {...props} />
);

const Screen = () => {
  const { data, isLoading, error, isFetching } = usePurchaseRequestsData();
  const purchaseRequests = data?.purchaseRequests || [];
  const totalCount = data?.totalCount || 0;
  const search = Route.useSearch();
  const router = useRouter();

  const columns = [
    { key: "sl_no", label: "SL No." },
    { key: "request_number", label: "Purchase Request Number" },
    { key: "item_count", label: "Item Count" },
    { key: "created_at", label: "Created At" },
    { key: "status", label: "Status" },
  ];
  const visibleColumns = columns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {} as Record<string, boolean>);

  return (
    <div className="p-0 min-h-screen">
      <div className="flex items-center justify-between mb-4">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink style={{ cursor: "pointer" }}>
                Reports Overview
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Purchase Requests List</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <hr className="my-4" />
        <div className="mt-4 border rounded-lg bg-white overflow-hidden">
          {isLoading ? (
            <TableLoading columns={columns} visibleColumns={visibleColumns} />
          ) : !data?.purchaseRequests || data.purchaseRequests.length === 0 ? (
            <div className="flex items-center justify-center p-8">
              <div className="text-sm text-gray-500">No entries found</div>
            </div>
          ) : (
            <>
              <Table className="text-sm">
          <TableHeader>
            <TableRow className="hover:bg-inherit">
              <StyledTableHead>SL No.</StyledTableHead>
              <StyledTableHead>Purchase Request Number</StyledTableHead>
              <StyledTableHead>Item Count</StyledTableHead>
              <StyledTableHead>Created At</StyledTableHead>
              <StyledTableHead>Status</StyledTableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {purchaseRequests.map((pr,i) => (<TableRow className="font-medium cursor-pointer" key={i} onClick={() => {
              router.navigate({
                to: "/inventory/purchase-request/$id",
                params: { id: pr.id },
                search: {
                  ...search,
                }
              })
            }}>
              <StyledTableCell>{i+1}</StyledTableCell>
              <StyledTableCell>{pr.requestNumber}</StyledTableCell>
              <StyledTableCell>{pr.items.length}</StyledTableCell>
              <StyledTableCell>{dayjs(pr.createdAt?.toDate()).format("DD MMM YYYY")}</StyledTableCell>
              <StyledTableCell>{
                  Object.keys(RequestStatus).find((key: string) => (RequestStatus as any)[key] === pr.status) || "Unknown"
                }</StyledTableCell>
            </TableRow>))}
          </TableBody>
        </Table>
        </>
          )}
        <div className="flex items-center justify-between py-4 px-6 text-sm border-t bg-white">
          <p>{data?.purchaseRequests && data.purchaseRequests.length > 0 ? totalCount : 0} <span className="text-gray-500 font-light">count</span></p>
          <div className="flex items-center gap-1">
            {/*
              Assume search.page is the current page (1-based), and data.totalCount/data.pageSize gives total pages.
              Adjust as per your actual search and data structure.
            */}
            {(() => {
              const page = Number(search.page) || 1;
              const pageSize = search.pageSize || 10;
              const totalPages = data?.totalCount ? Math.ceil(data.totalCount / pageSize) : 1;

              const goToPage = (p: number) => {
                router.navigate({
                  search: { ...search, page: p }
                });
              };

              return (
                <>
                  <PaginationButton disabled={page === 1} onClick={() => goToPage(1)}>
                    <span>{"<<"}</span>
                  </PaginationButton>
                  <PaginationButton disabled={page === 1} onClick={() => goToPage(page - 1)}>
                    <span>{"<"}</span>
                  </PaginationButton>
                  {[...Array(totalPages)].map((_, idx) => (
                    <PaginationButton
                      key={idx + 1}
                      active={page === idx + 1}
                      onClick={() => goToPage(idx + 1)}
                    >
                      <span className="text-sm">{idx + 1}</span>
                    </PaginationButton>
                  ))}
                  <PaginationButton disabled={page === totalPages} onClick={() => goToPage(page + 1)}>
                    <span>{">"}</span>
                  </PaginationButton>
                  <PaginationButton disabled={page === totalPages} onClick={() => goToPage(totalPages)}>
                    <span>{">>"}</span>
                  </PaginationButton>
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Screen;